import { useCallback, useEffect, useMemo, useState } from "react";
import {
  IRevenueCurrencyTotal, IRevenuePaymentMethodBreakdown, IRevenueReport,
  REVENUE_MAX_WINDOW_DAYS, REVENUE_SOURCE_META,
} from "../models/IRevenueReport";
import { getRevenueReport } from "../services/revenueReportService";
import { dineInErrorCode, dineInErrorDescription } from "../services/dineInService";

const pad = (n: number) => String(n).padStart(2, "0");
const toDateOnly = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function money(n: number, symbol?: string | null): string {
  return `${n.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${symbol ? ` ${symbol}` : ""}`;
}

function daysBetweenInclusive(from: string, to: string): number {
  const a = new Date(from + "T00:00:00");
  const b = new Date(to + "T00:00:00");
  return Math.floor((b.getTime() - a.getTime()) / 86400000) + 1;
}

function formatDateLabel(dateOnly: string): string {
  const d = new Date(dateOnly + "T00:00:00");
  return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric" });
}

type Preset = "today" | "yesterday" | "last7" | "thisMonth" | "custom";

function presetRange(p: Exclude<Preset, "custom">): { from: string; to: string } {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (p === "today") return { from: toDateOnly(today), to: toDateOnly(today) };
  if (p === "yesterday") {
    const y = new Date(today); y.setDate(y.getDate() - 1);
    return { from: toDateOnly(y), to: toDateOnly(y) };
  }
  if (p === "last7") {
    const s = new Date(today); s.setDate(s.getDate() - 6);
    return { from: toDateOnly(s), to: toDateOnly(today) };
  }
  const first = new Date(today.getFullYear(), today.getMonth(), 1);
  return { from: toDateOnly(first), to: toDateOnly(today) };
}

/** Aynı ödeme yöntemi id'si + para birimi, üç kanal boyunca toplanır (nakit tek satırdır). */
function reconcileByMethod(report: IRevenueReport) {
  const map = new Map<string, IRevenuePaymentMethodBreakdown & { sources: string[] }>();
  report.sources.forEach((s) => {
    s.paymentMethods.forEach((pm) => {
      const key = `${pm.paymentMethodId}|${pm.currencyId ?? "none"}`;
      const existing = map.get(key);
      if (existing) {
        existing.totalAmount += pm.totalAmount;
        existing.count += pm.count;
        if (!existing.sources.includes(String(s.source))) existing.sources.push(String(s.source));
      } else {
        map.set(key, { ...pm, sources: [String(s.source)] });
      }
    });
  });
  return Array.from(map.values()).sort((a, b) => b.totalAmount - a.totalAmount);
}

function CurrencyTotals({ totals, empty }: { totals: IRevenueCurrencyTotal[]; empty: string }) {
  if (totals.length === 0) return <div className="rev-zero">{empty}</div>;
  return (
    <div className="rev-currency-list">
      {totals.map((t, i) => (
        <div key={`${t.currencyId ?? "none"}-${i}`} className="rev-currency">
          <strong>{money(t.totalAmount, t.currencyDisplayText)}</strong>
          <span>{t.count} kayıt{!t.currencyId && " · para birimi yok"}</span>
        </div>
      ))}
    </div>
  );
}

interface Props {
  restaurantId: string;
}

export default function RevenueReportTab({ restaurantId }: Props) {
  const [preset, setPreset] = useState<Preset>("today");
  const [from, setFrom] = useState(toDateOnly(new Date()));
  const [to, setTo] = useState(toDateOnly(new Date()));
  const [report, setReport] = useState<IRevenueReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const windowDays = useMemo(() => (from && to ? daysBetweenInclusive(from, to) : 0), [from, to]);
  const rangeInvalid = !!from && !!to && (windowDays < 1 || windowDays > REVENUE_MAX_WINDOW_DAYS);

  const load = useCallback((f?: string, t?: string) => {
    setLoading(true);
    setError("");
    getRevenueReport(restaurantId, f, t)
      .then((res) => setReport(res.data))
      .catch((err) => {
        const code = dineInErrorCode(err);
        setReport(null);
        setError(
          code === "IncompleteDateRange" ? "Tarih aralığının iki ucu da seçilmeli."
          // InvalidDateRange açıklaması izin verilen gün sayısını kendisi taşır
          : code === "InvalidDateRange" ? (dineInErrorDescription(err) ?? `Aralık en fazla ${REVENUE_MAX_WINDOW_DAYS} gün olabilir.`)
          : code === "RestourantAccessDenied" ? "Bu restoranın ciro raporunu görme yetkiniz yok."
          : (dineInErrorDescription(err) ?? "Ciro raporu alınamadı.")
        );
      })
      .finally(() => setLoading(false));
  }, [restaurantId]);

  // İlk açılış: parametresiz çağrı = sunucunun "bugün"ü
  useEffect(() => { load(); }, [load]);

  const applyPreset = (p: Exclude<Preset, "custom">) => {
    const r = presetRange(p);
    setPreset(p);
    setFrom(r.from);
    setTo(r.to);
    load(r.from, r.to);
  };

  const applyCustom = () => {
    if (rangeInvalid) return;
    setPreset("custom");
    load(from, to);
  };

  const reconciled = report ? reconcileByMethod(report) : [];

  return (
    <div className="rev">
      {/* Aralık seçimi */}
      <div className="rev-toolbar">
        <div className="rev-presets">
          {([["today", "Bugün"], ["yesterday", "Dün"], ["last7", "Son 7 gün"], ["thisMonth", "Bu ay"]] as [Exclude<Preset, "custom">, string][])
            .map(([key, label]) => (
              <button key={key} type="button"
                className={`rev-chip${preset === key ? " rev-chip--active" : ""}`}
                onClick={() => applyPreset(key)} disabled={loading}>
                {label}
              </button>
            ))}
        </div>
        <div className="rev-range">
          <input type="date" value={from} max={to || undefined} onChange={(e) => { setFrom(e.target.value); setPreset("custom"); }} />
          <span>–</span>
          <input type="date" value={to} min={from || undefined} onChange={(e) => { setTo(e.target.value); setPreset("custom"); }} />
          <button type="button" className="dinein-btn dinein-btn--primary" onClick={applyCustom} disabled={loading || rangeInvalid || !from || !to}>
            {loading ? <span className="spinner-border spinner-border-sm" /> : <><i className="bi bi-search me-1"></i>Getir</>}
          </button>
        </div>
      </div>

      {rangeInvalid && (
        <div className="dinein-alert dinein-alert--warn">
          <i className="bi bi-exclamation-triangle-fill"></i>
          <span>
            {windowDays < 1
              ? "Bitiş tarihi başlangıçtan önce olamaz."
              : `Aralık en fazla ${REVENUE_MAX_WINDOW_DAYS} gün olabilir (şu an ${windowDays} gün).`}
          </span>
        </div>
      )}

      {error && (
        <div className="dinein-alert dinein-alert--error"><i className="bi bi-exclamation-circle-fill"></i><span>{error}</span></div>
      )}

      {loading && !report ? (
        <div className="dinein-center dinein-center--tall"><span className="spinner-border" /></div>
      ) : !report ? null : (
        <>
          {/* Genel toplam */}
          <div className="rev-hero">
            <div className="rev-hero__label">
              <i className="bi bi-cash-stack me-2"></i>
              Toplam Tahsilat
              <span className="rev-hero__range">
                {report.from === report.to
                  ? formatDateLabel(report.from)
                  : `${formatDateLabel(report.from)} – ${formatDateLabel(report.to)}`}
              </span>
            </div>
            {report.totals.length === 0 ? (
              <div className="rev-hero__amount rev-hero__amount--zero">Bu aralıkta tahsilat yok</div>
            ) : (
              <div className="rev-hero__amounts">
                {report.totals.map((t, i) => (
                  <div key={`${t.currencyId ?? "none"}-${i}`} className="rev-hero__amount">
                    {money(t.totalAmount, t.currencyDisplayText)}
                    <span>{t.count} kayıt</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Kanallar */}
          <div className="rev-sources">
            {report.sources.map((s) => {
              const meta = REVENUE_SOURCE_META[String(s.source)] ?? { label: String(s.source), icon: "bi-dot", color: "#6b7280", bg: "#f9fafb" };
              return (
                <div key={String(s.source)} className="rev-source">
                  <div className="rev-source__head" style={{ background: meta.bg }}>
                    <i className={`bi ${meta.icon}`} style={{ color: meta.color }}></i>
                    <span style={{ color: meta.color }}>{meta.label}</span>
                  </div>
                  <div className="rev-source__body">
                    <CurrencyTotals totals={s.totals} empty="Bu kanaldan tahsilat yok" />
                    {s.paymentMethods.length > 0 && (
                      <div className="rev-pm-list">
                        {s.paymentMethods.map((pm, i) => (
                          <div key={`${pm.paymentMethodId}-${pm.currencyId ?? "none"}-${i}`} className="rev-pm">
                            <span className="rev-pm__name">
                              {pm.isUnspecified && <i className="bi bi-question-circle me-1" title="Ödeme yöntemi kaydedilmemiş"></i>}
                              {pm.paymentMethodNameTr || pm.paymentMethodNameEn || "Bilinmeyen yöntem"}
                            </span>
                            <span className="rev-pm__amount">
                              {money(pm.totalAmount, pm.currencyDisplayText)}
                              <em>{pm.count}</em>
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Kasa denkleştirme */}
          {reconciled.length > 0 && (
            <div className="rev-reconcile">
              <div className="rev-reconcile__head">
                <i className="bi bi-calculator me-2"></i>
                Ödeme Yöntemine Göre (üç kanal birlikte)
              </div>
              <div className="rev-reconcile__list">
                {reconciled.map((pm, i) => (
                  <div key={`${pm.paymentMethodId}-${pm.currencyId ?? "none"}-${i}`} className="rev-reconcile__row">
                    <div>
                      <strong>
                        {pm.isUnspecified && <i className="bi bi-question-circle me-1"></i>}
                        {pm.paymentMethodNameTr || pm.paymentMethodNameEn || "Bilinmeyen yöntem"}
                      </strong>
                      <span className="rev-reconcile__sources">
                        {pm.sources.map((s) => REVENUE_SOURCE_META[s]?.label.split(" ")[0] ?? s).join(" · ")}
                      </span>
                    </div>
                    <div className="rev-reconcile__amount">
                      {money(pm.totalAmount, pm.currencyDisplayText)}
                      <span>{pm.count} kayıt</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="dinein-hint" style={{ padding: "0 14px 12px" }}>
                <i className="bi bi-info-circle me-1"></i>
                Nakit üç kanalda da aynı satırdır. Kart ise ayrı satırlardır (kuryenin POS'u ile kasanın POS'u) —
                bir "kart toplamı" istersen bu iki satırı kendin toplamalısın; rapor bilinçli olarak toplamıyor.
              </div>
            </div>
          )}

          {/* Notlar */}
          <div className="rev-notes">
            <div><i className="bi bi-info-circle me-1"></i> Bu rapor <strong>brüt tahsilattır</strong>; sistemde iade kavramı yok.</div>
            <div><i className="bi bi-calendar-check me-1"></i> Online/telefon siparişi <strong>verildiği güne</strong>, salon hesabı <strong>ödendiği güne</strong> yazılır. Bu yüzden salon geçmişi ile bir gün kayma görebilirsin.</div>
            <div><i className="bi bi-check2-circle me-1"></i> Yalnız teslim edilen siparişler ve ödenmiş salon hesapları sayılır; açık masa ve iptaller girmez.</div>
            <div><i className="bi bi-clock me-1"></i> Gün tanımı: {report.timeZoneId}.</div>
          </div>
        </>
      )}
    </div>
  );
}
